import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes, faSliders } from '@fortawesome/free-solid-svg-icons';

import type { StripeCheckoutMetadataEntry } from '@/@types/graphql-types';
import type { Device } from '@/helpers/composables/useUserAgent';
import type { SubscriptionInfo, SubscriptionTitles } from '@/features/jw-pro/types';

// Real per-variant Stripe products/prices
export const PRICING_EXP_SUBSCRIPTIONS: Record<
	Extract<SubscriptionTitles, `proPricingExpV2${string}`>,
	SubscriptionInfo
> = {
	proPricingExpV2Control: {
		id: 'prod_UwE42g3VLRPNQ1',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLc1KD0TTmBHo0FMOZ8kNM', price: 3.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant1Monthly: {
		id: 'prod_UwE5FOKO3TSBOQ',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLctKD0TTmBHo0XPkPNEIV', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant2Monthly: {
		id: 'prod_UwE5J06B2FaYEc',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLdEKD0TTmBHo0QAeSIMJ8', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant3Monthly: {
		id: 'prod_UwE5q5evVDUSBa',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLdiKD0TTmBHo0dlxFFJqH', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant3Yearly: {
		id: 'prod_UwE66Iew26TAGF',
		type: 'regular',
		timeframe: 'yearly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLeRKD0TTmBHo0fXfKV6IX', price: 9.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant4Monthly: {
		id: 'prod_UwE9jZTQonKoTx',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLh0KD0TTmBHo0Gtnc17CJ', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant4Lifetime: {
		id: 'prod_UwE9BH245yc49C',
		type: 'regular',
		timeframe: 'one time',
		prices: {
			DEFAULT: { priceId: 'price_1TwLheKD0TTmBHo0tYRmiONP', price: 24.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant5Monthly: {
		id: 'prod_UwEAoiUNal05cq',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLi4KD0TTmBHo01jBJpu0a', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant5Yearly: {
		id: 'prod_UwEAes8Y9vjcy5',
		type: 'regular',
		timeframe: 'yearly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLiSKD0TTmBHo0AzxDbowX', price: 9.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant5Lifetime: {
		id: 'prod_UwEBk7eLmiNKYG',
		type: 'regular',
		timeframe: 'one time',
		prices: {
			DEFAULT: { priceId: 'price_1TwLj6KD0TTmBHo0FNV4XOwc', price: 24.99, currency: 'EUR' },
		},
	},
	proPricingExpV2Variant6Monthly: {
		id: 'prod_UwECjbLhQbmIgp',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLjjKD0TTmBHo0yt2o5Vho', price: 0.99, currency: 'EUR' },
		},
	},
};

export type PlanKey = 'monthly' | 'yearly' | 'lifetime';

type Perk = {
	icon?: string;
	faIcon?: IconDefinition;
	titleKey: string;
	titleParams?: Record<string, string>;
	textKey: string;
	// hardcoded since the translation for WEBAPP_PRO_MODAL_BANNERS_SUBHEAD is missing its full stop
	textSuffix?: string;
};

type VariantSpec = {
	titleKey: string;
	badgeText?: string;
	tagline?: string;
	logoOverride?: string;
	ctaProvider?: string;
	perks: Perk[];
	showPlanSelector?: boolean;
	defaultPlanKey?: PlanKey;
	showYearlyOption?: boolean;
	showLifetimeOption?: boolean;
	showOriginalPrice?: boolean;
	monthlyPriceKey: SubscriptionTitles;
	yearlyPriceKey?: SubscriptionTitles;
	lifetimePriceKey?: SubscriptionTitles;
};

// Shared perks for variant_1 through variant_5 — only variant_6 swaps the second perk
const SHARED_PERKS: Perk[] = [
	{
		icon: `${ASSETS_DIR}/img/experiments/jw-pro-pricing/jwtv-free-badge.svg`,
		titleKey: 'WEBAPP_WATCH_FREE_ON_PROVIDER',
		titleParams: { provider: 'JustWatch TV' },
		textKey: 'WEBAPP_PRO_MODAL_FREE_AND_RENTALS',
	},
	{
		faIcon: faSliders,
		titleKey: 'WEBAPP_PRO_MODAL_WATCHLIST_FILTERS_HEAD',
		textKey: 'WEBAPP_PRO_MODAL_WATCHLIST_FILTERS_SUBHEAD',
	},
	{
		faIcon: faTimes,
		titleKey: 'WEBAPP_PRO_MODAL_BANNERS_HEAD',
		textKey: 'WEBAPP_PRO_MODAL_BANNERS_SUBHEAD',
		textSuffix: '.',
	},
];

// Copy per variant — pricing is always derived from the real SUBSCRIPTIONS constants in
// JwProPricingExpModal.vue, never hardcoded here.
export const VARIANT_SPECS: Partial<Record<string, VariantSpec>> = {
	// v1: shows the regular price struck through alongside the new discounted price
	variant_1: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showOriginalPrice: true,
		monthlyPriceKey: 'proPricingExpV2Variant1Monthly',
	},
	// v2: same as variant_1 but without the struck-through original price
	variant_2: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		monthlyPriceKey: 'proPricingExpV2Variant2Monthly',
	},
	// v3: same as variant_1 but with a monthly/yearly plan selector instead of a single price
	variant_3: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showYearlyOption: true,
		defaultPlanKey: 'yearly',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpV2Variant3Monthly',
		yearlyPriceKey: 'proPricingExpV2Variant3Yearly',
	},
	// v4: same as v3 but the second plan is a Lifetime one-time payment instead of Yearly
	variant_4: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showLifetimeOption: true,
		defaultPlanKey: 'lifetime',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpV2Variant4Monthly',
		lifetimePriceKey: 'proPricingExpV2Variant4Lifetime',
	},
	// v5: same as v3/v4 but shows all three plans together (Monthly, Lifetime, Yearly),
	// with Lifetime as the biggest/default-selected plan
	variant_5: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showLifetimeOption: true,
		showYearlyOption: true,
		defaultPlanKey: 'lifetime',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpV2Variant5Monthly',
		yearlyPriceKey: 'proPricingExpV2Variant5Yearly',
		lifetimePriceKey: 'proPricingExpV2Variant5Lifetime',
	},
	// v6: same pricing as variant_2, only the copy changes to describe "JustWatch TV Plus"
	// instead of "JustWatch Pro"
	variant_6: {
		titleKey: 'WEBAPP_PRO_MODAL_UPGRADE_EXPERIENCE',
		logoOverride: `${ASSETS_DIR}/img/experiments/jw-pro-pricing/jwtv-logo-plus.svg`,
		ctaProvider: 'JustWatch TV Plus',
		monthlyPriceKey: 'proPricingExpV2Variant6Monthly',
		perks: [
			{
				icon: `${ASSETS_DIR}/img/experiments/jw-pro-pricing/jwtv-free-badge.svg`,
				titleKey: 'WEBAPP_WATCH_FREE_ON_PROVIDER',
				titleParams: { provider: 'JustWatch TV' },
				textKey: 'WEBAPP_PRO_MODAL_MOVIES_SHOWS',
			},
			{
				icon: `${ASSETS_DIR}/img/experiments/jw-pro-pricing/ticket-icon.svg`,
				titleKey: 'WEBAPP_PRO_MODAL_RENTALS_HEAD',
				textKey: 'WEBAPP_PRO_MODAL_RENTALS_SUBHEAD',
			},
			{
				faIcon: faTimes,
				titleKey: 'WEBAPP_PRO_MODAL_BANNERS_HEAD',
				textKey: 'WEBAPP_PRO_MODAL_BANNERS_SUBHEAD',
			},
		],
	},
};

// Matches the tracking schema's `value` field convention (0 = web, 1 = web_mobile).
export function getTrackingDeviceValue(deviceType: Device): 0 | 1 {
	return deviceType === 'mobile' ? 1 : 0;
}

const DEVICE_METADATA_VALUES: Record<Device, string> = {
	desktop: 'web',
	mobile: 'webmob',
	unknown: 'unknown',
};

// variant is null outside the experiment (no metadata to attach); a real value — including
// 'control' — means the checkout should carry RevenueCat/Stripe metadata for this experiment.
export function buildCheckoutMetadata(
	variant: string | null,
	deviceType: Device
): StripeCheckoutMetadataEntry[] | undefined {
	if (!variant) return undefined;

	return [
		{ key: 'device', value: DEVICE_METADATA_VALUES[deviceType] },
		{ key: 'experimentName', value: 'EXP_JW_PRO_PRICING' },
		{ key: 'experimentVariant', value: variant },
	];
}
